import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import {
  LayoutDashboard, Users, QrCode, FileBarChart, Building2, UserCog, Bell, LogOut, ShieldCheck,
  PanelLeftClose, PanelLeftOpen,
} from "lucide-react";

const ROLE_LABELS = {
  super_admin: "TAG Super Admin",
  client_admin: "Client Administrator",
  provider: "Provider",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [brand, setBrand] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (user?.client_id) api.get("/clients").then((r) => setBrand(r.data[0] || null)).catch(() => {});
  }, [user]);

  useEffect(() => {
    document.title = brand?.name ? `${brand.name} · TAGVerify` : "TAGVerify";
    const fav = document.querySelector("link[rel='icon']");
    if (fav && brand?.logo_url) fav.href = brand.logo_url;
  }, [brand]);

  const brandColor = brand?.primary_color || "#0D6E6E";

  useEffect(() => {
    const load = () =>
      api.get("/dashboard").then((r) => setUnread(r.data.stats.unread_alerts)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const nav = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "client_admin"] },
    { to: "/clients", label: user?.role === "super_admin" ? "Clients" : "Organization", icon: Building2, roles: ["super_admin", "client_admin"] },
    { to: "/students", label: "Students", icon: Users, roles: ["super_admin", "client_admin", "provider"] },
    { to: "/users", label: "Users", icon: UserCog, roles: ["super_admin", "client_admin"] },
    { to: "/qr-codes", label: "QR Codes", icon: QrCode, roles: ["super_admin", "client_admin"] },
    { to: "/reports", label: "Reports", icon: FileBarChart, roles: ["super_admin", "client_admin"] },
    { to: "/alerts", label: "Alerts", icon: Bell, roles: ["super_admin", "client_admin", "provider"], badge: unread },
  ].filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-slate-200/70 bg-white transition-all duration-200 md:flex ${collapsed ? "w-[76px]" : "w-64"}`} data-testid="sidebar">
        <div className={`flex items-center gap-2 border-b border-slate-100 py-5 ${collapsed ? "justify-center px-2" : "px-5"}`}>
          {brand?.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="h-9 w-9 rounded-xl object-contain" data-testid="client-logo" />
          ) : (
            <img src={collapsed ? "/tagverify-icon.png" : "/tagverify-logo.png"} alt="TAGVerify"
              className={collapsed ? "h-9 w-9 object-contain" : "h-10 w-auto object-contain"} data-testid="tagverify-logo" />
          )}
          {!collapsed && brand && (
            <div className="min-w-0">
              <div className="truncate font-display text-lg font-bold tracking-tight text-slate-900" data-testid="portal-title">
                {brand.name}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                Powered by TAGVerify
              </div>
            </div>
          )}
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {!collapsed && <div className="kicker mb-2 px-3">Menu</div>}
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              title={n.label}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              style={({ isActive }) => (isActive ? { backgroundColor: `${brandColor}14`, color: brandColor } : undefined)}
              className={({ isActive }) =>
                `flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  collapsed ? "justify-center" : "justify-between"
                } ${isActive ? "font-semibold" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"}`
              }
            >
              <span className={`flex items-center ${collapsed ? "" : "gap-3"}`}>
                <n.icon size={18} /> {!collapsed && n.label}
              </span>
              {!collapsed && n.badge > 0 && (
                <span className="rounded-full bg-red-500 px-2 py-0.5 text-[10px] font-bold text-white" data-testid="alerts-badge">
                  {n.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <button onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-button"
          className={`mx-3 mb-2 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-slate-400 transition-colors duration-200 hover:bg-slate-50 hover:text-slate-700 ${collapsed ? "justify-center" : ""}`}>
          {collapsed ? <PanelLeftOpen size={17} /> : <><PanelLeftClose size={17} /> Collapse</>}
        </button>
        <div className="border-t border-slate-100 p-4">
          {!collapsed && (
            <div className="mb-3">
              <div className="text-sm font-semibold text-slate-900" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500">{ROLE_LABELS[user?.role]}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            title="Sign out"
            className={`flex w-full items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:bg-slate-50 ${collapsed ? "justify-center" : ""}`}
          >
            <LogOut size={15} /> {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      <div className={`flex min-w-0 flex-1 flex-col transition-all duration-200 ${collapsed ? "md:pl-[76px]" : "md:pl-64"}`}>
        <header className="sticky top-0 z-20 hidden items-center justify-between border-b border-slate-200 bg-white/80 px-8 py-2.5 backdrop-blur-xl md:flex" data-testid="topbar">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-slate-400">{brand ? brand.name : "TAGVerify"}</span>
            <span className="text-slate-300">/</span>
            <span className="font-semibold text-slate-900">
              {(nav.find((n) => (n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to))) || {}).label || "Portal"}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-slate-400" data-testid="topbar-date">
              {new Date().toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </span>
            <div className="flex items-center gap-2.5 rounded-full border border-slate-200 py-1 pl-1 pr-3" data-testid="topbar-user">
              <div className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: brandColor }}>
                {(user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
              </div>
              <span className="text-xs font-semibold text-slate-700">{user?.name}</span>
            </div>
          </div>
        </header>
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-xl md:hidden">
          <div className="flex items-center gap-2">
            <img src="/tagverify-logo.png" alt="TAGVerify" className="h-7 w-auto object-contain" />
          </div>
          <button onClick={handleLogout} data-testid="mobile-logout-button" className="text-slate-500">
            <LogOut size={18} />
          </button>
        </header>
        <main className="flex-1 p-4 pb-28 md:p-8 md:pb-8">
          <Outlet />
        </main>
        <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-slate-200/80 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" data-testid="mobile-bottom-nav">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              style={({ isActive }) => (isActive ? { color: brandColor } : undefined)}
              className={({ isActive }) =>
                `relative flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-semibold ${isActive ? "" : "text-slate-400"}`
              }
            >
              <n.icon size={20} />
              {n.label}
              {n.badge > 0 && <span className="absolute right-1/4 top-1 h-2 w-2 rounded-full bg-red-500" />}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
